/**
 * CRM Notes Query Hooks
 *
 * TanStack Query hooks for the cross-entity CRM note feed.
 *
 * INVALIDATION CONTRACT: the list query key is exactly
 * `['crm-notes', entityType, entityId]`. Every mutation invalidates that same
 * key, so the entity-level invalidate prefix-matches the live list query and
 * the feed refreshes immediately after create/update/delete.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import {
  getNotes,
  createNote,
  updateNote,
  deleteNote,
  type CrmNote,
  type CrmNoteEntityType,
} from '../api/crmNotesApi';
import { getErrorMessage } from '../utils/getErrorMessage';

// =============================================================================
// Query Key Factory
// =============================================================================

export const crmNoteKeys = {
  all: ['crm-notes'] as const,
  byEntity: (entityType: CrmNoteEntityType, entityId: string) =>
    [...crmNoteKeys.all, entityType, entityId] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch all notes for an entity (newest first).
 */
export function useEntityNotes(entityType: CrmNoteEntityType, entityId: string) {
  return useQuery<CrmNote[]>({
    queryKey: crmNoteKeys.byEntity(entityType, entityId),
    queryFn: () => getNotes(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Create a note on an entity.
 */
export function useCreateNote(entityType: CrmNoteEntityType, entityId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bodyHtml: string) => createNote(entityType, entityId, bodyHtml),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmNoteKeys.byEntity(entityType, entityId) });
      toast.success(i18next.t('crm:notes.toast.added'));
    },
    onError: (error) => {
      toast.error(i18next.t('crm:notes.toast.failedAdd'), { description: getErrorMessage(error) });
    },
  });
}

/**
 * Update a note's body.
 */
export function useUpdateNote(entityType: CrmNoteEntityType, entityId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ noteId, bodyHtml }: { noteId: string; bodyHtml: string }) =>
      updateNote(noteId, bodyHtml),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmNoteKeys.byEntity(entityType, entityId) });
      toast.success(i18next.t('crm:notes.toast.updated'));
    },
    onError: (error) => {
      toast.error(i18next.t('crm:notes.toast.failedUpdate'), { description: getErrorMessage(error) });
    },
  });
}

/**
 * Delete a note.
 */
export function useDeleteNote(entityType: CrmNoteEntityType, entityId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: string) => deleteNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmNoteKeys.byEntity(entityType, entityId) });
      toast.success(i18next.t('crm:notes.toast.deleted'));
    },
    onError: (error) => {
      toast.error(i18next.t('crm:notes.toast.failedDelete'), { description: getErrorMessage(error) });
    },
  });
}
